import { useEffect, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { useSelector } from 'react-redux';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { Box, Button, Link, Typography } from '@mui/material';
import { IconButton } from '@mui/material';

import { AlertMessage } from '~/pages/CreateQR/sub-components/pdfTemplate/sub-components/AlertMessage';
import { getPdfReducer } from '~/store/reducers/previewScreens/pdf/selectors';
import { COLORS } from '~/styles/colors';
import { mainPreviewContainerStyles } from '~/styles/common';
import { isMobileResolution } from '~/utils/common';

import PreviewSmallPdfViewer, {
  ARROW_BUTTON_GAP_SIZE,
  ARROW_BUTTON_SIZE,
} from '../../../../pdfViewer/preview-small-pdf-viewer/PreviewSmallPdfViewer';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

type PDF_TEMPLATE_0Props = {
  isMyMicrosite?: boolean;
};
export function PDF_TEMPLATE_0({ isMyMicrosite }: PDF_TEMPLATE_0Props) {
  const pdfState = useSelector(getPdfReducer);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const {
    bacgkroundColor,
    micrositeStyles,
    fonts: { head, text },
    basicInformation: { images },
    pdfInformation: { company, title, description, website, button },
  } = pdfState;

  useEffect(() => {
    if (currentIndex >= images.length) {
      if (images.length > 0) {
        setCurrentIndex(images.length - 1);
      } else {
        setCurrentIndex(0);
      }
    }
  }, [images.length, currentIndex]);

  return (
    <Box
      sx={{
        ...mainPreviewContainerStyles,
        alignItems: 'normal',
        background: `linear-gradient(40deg, ${bacgkroundColor[1]} 0%, ${bacgkroundColor[1]} 50%, ${bacgkroundColor[0]} 50%);`,
        ...micrositeStyles?.container,
        margin: isMyMicrosite ? undefined : '0 12px',
        width: isMyMicrosite ? '100%' : 'calc(100% - 24px)',
        height: 'calc(100% - 28px)',
        ...(isMyMicrosite ? { borderRadius: 0, minHeight: '100dvh' } : {}),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          minWidth: '100%',
          position: 'relative',
          height: '100%',
          minHeight: 'calc(100dvh - 28px)',
        }}
      >
        {!!images.length || company || title || description || website ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ padding: '0 16px' }}>
              <Typography
                sx={{
                  fontFamily: text,
                  fontSize: 12,
                  mt: 1,
                  mb: 2,
                  wordBreak: 'break-word',
                  minHeight: company ? 'auto' : '12px',
                  color: COLORS.white,
                }}
              >
                {company}
              </Typography>

              <Typography
                sx={{
                  fontFamily: head,
                  fontSize: 20,
                  mb: 2,
                  wordBreak: 'break-word',
                  minHeight: title ? 'auto' : '20px',
                  color: COLORS.white,
                }}
              >
                {title}
              </Typography>

              <Typography
                sx={{
                  fontFamily: text,
                  fontSize: 12,
                  mb: 3,
                  wordBreak: 'break-word',
                  minHeight: description ? 'auto' : '12px',
                  color: COLORS.white,
                }}
              >
                {description}
              </Typography>
            </Box>
            {!!images.length && (
              <Box
                sx={{
                  position: 'relative',
                  width: 'calc(100% - 32px)',
                  padding: '20px 10px',
                  backgroundColor: COLORS.white,
                  borderRadius: '13px',
                  boxShadow: `0 2.6px 13px 0 ${COLORS.textMainOpacity15}`,
                  margin: '0 16px',
                }}
              >
                {images.length > 1 && (
                  <>
                    <IconButton
                      onClick={handlePrev}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: ARROW_BUTTON_GAP_SIZE,
                        height: ARROW_BUTTON_SIZE,
                        width: ARROW_BUTTON_SIZE,
                        zIndex: 1000,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '50%',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        transform: 'translateY(-50%) rotate(180deg)',
                        '&:hover': {
                          backgroundColor: 'rgb(241 237 237)',
                        },
                      }}
                    >
                      <ArrowForwardIosIcon />
                    </IconButton>
                    <IconButton
                      onClick={handleNext}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: ARROW_BUTTON_GAP_SIZE,
                        height: ARROW_BUTTON_SIZE,
                        width: ARROW_BUTTON_SIZE,
                        zIndex: 1000,
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '50%',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgb(241 237 237)',
                        },
                      }}
                    >
                      <ArrowForwardIosIcon />
                    </IconButton>
                  </>
                )}
                <PreviewSmallPdfViewer link={images[currentIndex]?.previewPdfLink} />
                {!!images.length && (
                  <Button
                    startIcon={<RemoveRedEyeIcon />}
                    fullWidth
                    disableRipple
                    sx={{
                      padding: '0.4rem 1.2rem',
                      mt: 2,
                      marginInline: 'auto',
                      color: COLORS.white,
                      background: bacgkroundColor[0],
                      borderRadius: '100px',
                      fontFamily: text,
                      maxWidth: '220px',
                      '&:hover': {
                        backgroundColor: bacgkroundColor[0],
                      },
                      ...(isMobileResolution && {
                        '&:hover': {
                          backgroundColor: bacgkroundColor[0],
                        },
                      }),
                      wordBreak: 'break-word',
                    }}
                    onClick={() => window.open(images[currentIndex]?.previewPdfLink, '_blank')}
                  >
                    {button}
                  </Button>
                )}
              </Box>
            )}
            <Typography sx={{ fontFamily: text, fontSize: 13, mt: 2 }}>
              <Link
                href={website}
                target="_blank"
                sx={{ textDecoration: 'none', color: COLORS.purple, wordBreak: 'break-word', pl: 1, pr: 1 }}
              >
                {website}
              </Link>
            </Typography>
          </Box>
        ) : (
          <AlertMessage />
        )}
      </Box>
    </Box>
  );
}
